import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { $, fH, fN, monthLabel, MONTH_NAMES } from './utils';
import TeamTab from './TeamTab';
import DepartmentsTab from './DepartmentsTab';
import ClientsTab from './ClientsTab';
import CostTab from './CostTab';
import PtoTab from './PtoTab';
import DirectReportsTab from './DirectReportsTab';
import { MenuLink } from './PageHeader';
import DisplayToggles from './DisplayToggles';

const TABS = ['Team Members', 'Departments', 'Client P&L', 'Cost & Utilization', 'PTO & Absence'];
const DIRECT_REPORTS_TAB = 'Direct Reports';

export default function Dashboard({ session, onSignOut }) {
  const [activeTab,  setActiveTab]  = useState(0);
  const [months,     setMonths]     = useState([]);   // [{period_year, period_month, label}]
  const [selMonth,   setSelMonth]   = useState(null); // {year, month}
  const [summary,    setSummary]    = useState(null);
  const [trend,      setTrend]      = useState([]);
  const [users,      setUsers]      = useState([]);
  const [clients,    setClients]    = useState([]);
  const [overrides,  setOverrides]  = useState([]);
  const [pto,        setPto]        = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [excludeOps, setExcludeOps] = useState(false);
  const [directCost, setDirectCost] = useState(false);
  const [reportRows, setReportRows] = useState([]); // signed-in leader's direct reports, by week

  // Load available months on mount
  useEffect(() => {
    supabase.rpc('get_available_months').then(({ data, error }) => {
      if (error || !data?.length) return;
      setMonths(data);
      setSelMonth({ year: data[0].period_year, month: data[0].period_month });
    });
    supabase.rpc('get_trend_summary', { months_back: 3 }).then(({ data }) => {
      if (data) setTrend([...data].reverse()); // oldest first for trend display
    });
    // Scoped server-side to people.leader_email = the signed-in user; empty for non-leaders.
    supabase.rpc('direct_reports_weekly', { p_weeks: 12 }).then(({ data }) => {
      if (data) setReportRows(data);
    });
  }, []);

  const tabs = reportRows.length ? [...TABS, DIRECT_REPORTS_TAB] : TABS;

  // Load data when selected month changes
  useEffect(() => {
    if (!selMonth) return;
    setLoading(true);
    const { year: yr, month: mo } = selMonth;

    Promise.all([
      supabase.from('monthly_summary').select('*').eq('period_year', yr).eq('period_month', mo).single(),
      supabase.from('team_analytics').select('*').eq('period_year', yr).eq('period_month', mo),
      supabase.from('clients_raw').select('*').eq('period_year', yr).eq('period_month', mo),
      supabase.from('active_overrides').select('*').eq('period_year', yr).eq('period_month', mo),
      supabase.from('pto_monthly').select('*').eq('period_year', yr).eq('period_month', mo),
    ]).then(([sumRes, usersRes, clientsRes, ovRes, ptoRes]) => {
      setSummary(sumRes.data);
      setUsers(usersRes.data || []);
      setClients(clientsRes.data || []);
      setOverrides(ovRes.data || []);
      setPto(ptoRes.data || []);
      setLoading(false);
    });
  }, [selMonth]);

  const staleOverrides = overrides.filter(o => o.is_stale);

  // Derived: active users respecting excludeOps toggle
  const EXCLUDED_NAMES = new Set(['Diego Prusky','Bruni Salmeron','Chuny Coletti','Ro Zicarelli']);
  const EXCLUDED_POSITIONS = new Set(['New Business']);
  const isExcluded = u => EXCLUDED_NAMES.has(u.user_name) || EXCLUDED_POSITIONS.has(u.position);
  const activeUsers = excludeOps ? users.filter(u => !isExcluded(u)) : users;

  // pto_monthly exposes `person` / `department` where team_analytics uses
  // `user_name` / `position`, so the same exclusion is applied on those fields.
  const activePto = excludeOps
    ? pto.filter(p => !EXCLUDED_NAMES.has(p.person) && !EXCLUDED_POSITIONS.has(p.department))
    : pto;

  return (
    <div style={{ maxWidth:1480, margin:'0 auto', padding:'24px 24px 60px' }}>
      {/* ── Header ── */}
      <header style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:18, flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <MenuLink />
          <div style={{
            width:32, height:32, borderRadius:7,
            background:'linear-gradient(135deg,#16a34a,#2563eb)',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <span style={{ color:'#fff', fontWeight:800, fontSize:14 }}>IP</span>
          </div>
          <h1 style={{ fontSize:22, fontWeight:700, letterSpacing:'-0.4px' }}>
            INPULSE <span style={{ color:'var(--green)' }}>Core Operations</span>
          </h1>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          {/* Month picker */}
          <select
            value={selMonth ? `${selMonth.year}-${selMonth.month}` : ''}
            onChange={e => {
              const [y, m] = e.target.value.split('-').map(Number);
              setSelMonth({ year:y, month:m });
            }}
            style={selectStyle}
          >
            {months.map(m => (
              <option key={`${m.period_year}-${m.period_month}`} value={`${m.period_year}-${m.period_month}`}>
                {MONTH_NAMES[m.period_month]} {m.period_year}
              </option>
            ))}
          </select>
          <DisplayToggles />
          {/* User */}
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px', background:'var(--card)', border:'1px solid var(--border)', borderRadius:8, fontSize:13 }}>
            <div style={{ width:24, height:24, borderRadius:'50%', background:'linear-gradient(135deg,#16a34a,#2563eb)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontSize:10, fontWeight:700 }}>{session.user.email[0].toUpperCase()}</span>
            </div>
            <span style={{ color:'var(--text2)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{session.user.email}</span>
            <button onClick={onSignOut} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:12, marginLeft:4 }}>Sign out</button>
          </div>
        </div>
      </header>

      {/* ── Stale overrides banner ── */}
      {staleOverrides.length > 0 && (
        <div style={{ background:'var(--warn-bg)', border:'1px solid var(--warn-border)', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:13, color:'var(--warn-text)', display:'flex', alignItems:'center', gap:8 }}>
          <span>⚠️</span>
          <span><strong>{staleOverrides.length} override{staleOverrides.length>1?'s':''}</strong> may be stale — the raw data now matches the corrected value. Review in the Overrides panel.</span>
        </div>
      )}

      {/* ── Trend strip (3 months) ── */}
      {trend.length > 1 && (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${trend.length},1fr)`, gap:12, marginBottom:20 }}>
          {trend.map((t, i) => {
            const isCur = selMonth && t.period_year===selMonth.year && t.period_month===selMonth.month;
            const prev = trend[i-1];
            const revDelta = prev ? ((t.total_revenue_attributed - prev.total_revenue_attributed) / prev.total_revenue_attributed * 100) : null;
            return (
              <button
                key={`${t.period_year}-${t.period_month}`}
                onClick={() => setSelMonth({ year:t.period_year, month:t.period_month })}
                style={{
                  background: isCur ? 'var(--card)' : 'var(--card2)',
                  border: `1px solid ${isCur ? 'var(--green)' : 'var(--border)'}`,
                  borderRadius:10, padding:'14px 16px', textAlign:'left', cursor:'pointer',
                  boxShadow: isCur ? '0 0 0 1px var(--green)' : 'none',
                  transition:'.15s',
                }}
              >
                <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.7px', color: isCur ? 'var(--green)' : 'var(--text3)', marginBottom:6 }}>
                  {monthLabel(t.period_year, t.period_month)}
                </div>
                <div style={{ fontFamily:'DM Mono,monospace', fontSize:18, fontWeight:700, marginBottom:2 }}>
                  {$(t.total_revenue_attributed)}
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', display:'flex', gap:12 }}>
                  <span>{$(t.total_profit_contributed)} profit</span>
                  {revDelta != null && (
                    <span style={{ color: revDelta>=0 ? 'var(--green)' : 'var(--red)' }}>
                      {revDelta>=0?'+':''}{fN(revDelta, 1)}% rev
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ── KPIs ── */}
      {summary && !loading && <KpiRow summary={summary} users={activeUsers} excludeOps={excludeOps} />}

      {/* ── Toggles ── */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <Toggle
          checked={excludeOps}
          onChange={setExcludeOps}
          label="Exclude non-billable roles"
          sub="Operations + New Business removed from totals"
        />
        <Toggle
          checked={directCost}
          onChange={setDirectCost}
          label="Direct cost view"
          sub="Shows hours × rate only; gap cost stays as org overhead"
        />
      </div>

      {/* ── Tabs ── */}
      <div style={{ borderBottom:'2px solid var(--border)', display:'flex', marginBottom:0 }}>
        {tabs.map((t, i) => (
          <button
            key={t}
            onClick={() => setActiveTab(i)}
            style={{
              padding:'10px 22px', fontSize:13, fontWeight:600, background:'none', border:'none',
              borderBottom: activeTab===i ? '2px solid var(--green)' : '2px solid transparent',
              marginBottom:-2, cursor:'pointer',
              color: activeTab===i ? 'var(--green)' : 'var(--text2)',
              transition:'.15s',
            }}
          >{t}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:60, textAlign:'center', color:'var(--text3)' }}>Loading {selMonth ? monthLabel(selMonth.year, selMonth.month) : ''}…</div>
      ) : (
        <div style={{ paddingTop:20 }}>
          {activeTab === 0 && <TeamTab users={activeUsers} overrides={overrides} excludeOps={excludeOps} directCost={directCost} />}
          {activeTab === 1 && <DepartmentsTab users={activeUsers} />}
          {activeTab === 2 && <ClientsTab clients={clients} users={activeUsers} excludeOps={excludeOps} directCost={directCost} />}
          {activeTab === 3 && <CostTab users={activeUsers} summary={summary} pto={activePto} />}
          {activeTab === 4 && <PtoTab pto={activePto} users={activeUsers} excludeOps={excludeOps} />}
          {tabs[activeTab] === DIRECT_REPORTS_TAB && <DirectReportsTab rows={reportRows} session={session} />}
        </div>
      )}
    </div>
  );
}

function KpiRow({ summary, users, excludeOps }) {
  const s = excludeOps ? {
    ...summary,
    total_users_active: users.length,
    total_cost: users.reduce((a,u)=>a+(u.total_cost||0),0),
    total_revenue_attributed: users.reduce((a,u)=>a+(u.revenue_attributed||0),0),
    total_profit_contributed: users.reduce((a,u)=>a+(u.profit_contributed||0),0),
    total_hours: users.reduce((a,u)=>a+(u.total_hours_worked||0),0),
    total_billable_hours: users.reduce((a,u)=>a+(u.billable_hours||0),0),
  } : summary;

  const margin = s.total_revenue_attributed > 0
    ? s.total_profit_contributed / s.total_revenue_attributed * 100
    : null;
  const billRatio = s.total_hours > 0
    ? s.total_billable_hours / s.total_hours * 100
    : null;

  const kpis = [
    { label:'Team Members',   value:s.total_users_active,          sub:`${summary.salaried_count}S · ${summary.freelancer_count}FL`,  color:'var(--blue)' },
    { label:'Total Hours',    value:fH(s.total_hours),             sub:`${fH(s.total_billable_hours)}h billable`,                     color:'var(--text)' },
    { label:'Billable Ratio', value:billRatio != null ? `${fN(billRatio, 1)}%`:'—', sub:'of total hours',                                           color: Math.round(billRatio*10)/10>=70?'var(--green)':'var(--amber)' },
    { label:'Avg Utilization',value:s.avg_utilization_pct ? `${fN(parseFloat(s.avg_utilization_pct), 1)}%`:'—', sub:'billable / contracted', color:'var(--purple)' },
    { label:'Total Cost',     value:$(s.total_cost),               sub:'labor, 0% overhead',                                         color:'var(--amber)' },
    { label:'Revenue',        value:$(s.total_revenue_attributed), sub:'attributed by hours',                                        color:'var(--blue)' },
    { label:'Profit',         value:$(s.total_profit_contributed), sub: margin != null ? `${fN(margin, 1)}% margin` : 'revenue − cost',             color: s.total_profit_contributed>=0?'var(--green)':'var(--red)' },
  ];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
      {kpis.map(k => (
        <div key={k.label} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 18px' }}>
          <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.8px', color:'var(--text2)', marginBottom:6, fontWeight:500 }}>{k.label}</div>
          <div style={{ fontSize:24, fontWeight:700, fontFamily:'DM Mono,monospace', color:k.color, lineHeight:1.1 }}>{k.value}</div>
          <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

function Toggle({ checked, onChange, label, sub }) {
  return (
    <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 16px', userSelect:'none' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width:36, height:20, borderRadius:10, position:'relative', transition:'.2s', flexShrink:0,
          background: checked ? 'var(--green)' : 'var(--border)',
        }}
      >
        <div style={{
          position:'absolute', left: checked ? 18 : 2, top:2,
          width:16, height:16, borderRadius:'50%', background:'#fff',
          boxShadow:'0 1px 3px rgba(0,0,0,.15)', transition:'.2s',
        }} />
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:500 }}>{label}</div>
        <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'DM Mono,monospace' }}>{sub}</div>
      </div>
    </label>
  );
}

const selectStyle = {
  background:'var(--card)', border:'1px solid var(--border)', borderRadius:8,
  padding:'8px 14px', color:'var(--text)', fontSize:13, cursor:'pointer', fontWeight:600,
};
